// ui/Sheet/Sheet.tsx — slide-in panel + scrim. Mirrors Dialog's focus-trap/escape/a11y.
//
// side='bottom'  → slides up from the bottom (compose sheet, sort menus)
// side='right'   → slides in from the right (right-rail drawer)
// side='center'  → centered on desktop, bottom sheet on mobile without remounting children
//
// • Closes on scrim-click + Escape.
// • Focus trap: Tab wraps within the surface; focus restores on close.
// • Respects prefers-reduced-motion (no slide animation when reduced-motion).
// • role="dialog" aria-modal="true" — aria-labelledby when title prop given.

import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { Icon } from '@/icons';
import { usePreferences } from '@/hooks/usePreferences';

export interface SheetProps {
  side: 'bottom' | 'right' | 'center';
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** side='right' only — CSS width, e.g. 'min(480px, 92vw)'. Defaults to the
   * existing right-rail-drawer width when omitted, so every current caller
   * is unaffected. */
  width?: string;
  closeLabel?: string;
  /** Optional heading/actions row. Label its heading with the supplied title ID. */
  renderHeader?: (titleId: string) => ReactNode;
}

// ── Animations ────────────────────────────────────────────────────────────────

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
`;

const slideRight = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

// ── Styled pieces ──────────────────────────────────────────────────────────────

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(4, 5, 12, 0.72);
  backdrop-filter: blur(3px);
`;

const BottomSurface = styled.div<{ $width?: string }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 201;
  max-height: 90dvh;
  overflow-y: auto;
  background: ${({ theme }) => theme.color.surfaceRaised};
  background-image: ${({ theme }) => theme.surfaceGradients.overlay};
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  border-bottom: none;
  border-radius: ${({ theme }) => theme.radius.lg} ${({ theme }) => theme.radius.lg} 0 0;
  box-shadow: ${({ theme }) => theme.elevation.md};
  padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
  outline: none;
  &[data-custom-header='true'] {
    padding: 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${slideUp} 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }
`;

const RightSurface = styled.div<{ $width?: string }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 201;
  width: ${({ $width }) => $width ?? 'min(360px, 90vw)'};
  overflow-y: auto;
  background: ${({ theme }) => theme.color.surfaceRaised};
  background-image: ${({ theme }) => theme.surfaceGradients.overlay};
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  border-right: none;
  box-shadow: ${({ theme }) => theme.elevation.md};
  padding: 20px;
  outline: none;
  &[data-custom-header='true'] {
    padding: 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${slideRight} 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }
`;

const SheetTitle = styled.h2`
  margin: 0 0 16px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.color.textPrimary};
`;
const HeaderBody = styled.div`
  padding: 16px 20px calc(20px + env(safe-area-inset-bottom));
`;

const CenterSurface = styled(BottomSurface)`
  &:has([data-testid='composer']) > h2 {
    font-size: 28px;
    margin-bottom: 8px;
  }
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%);
  width: ${({ $width }) => $width ?? 'min(680px, calc(100vw - 40px))'};
  max-height: calc(100dvh - 48px);
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 24px;
  animation: none;
  @media (max-width: 767px) {
    inset: auto 0 0;
    transform: none;
    width: auto;
    max-height: 90dvh;
    border-bottom: none;
    border-radius: ${({ theme }) => theme.radius.lg} ${({ theme }) => theme.radius.lg} 0 0;
    padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
  }
`;
const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  top: 14px;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: transparent;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Handle = styled.div<{ $responsive?: boolean }>`
  display: ${({ $responsive }) => ($responsive ? 'none' : 'block')};
  @media (max-width: 767px) {
    display: block;
  }
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.color.glassBorder};
  margin: 0 auto 16px;
`;

// ── Shared focus-trap logic (extracted so both surfaces use it) ────────────────

function useFocusTrap(open: boolean, onClose: () => void) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<HTMLElement | null>(null);
  // A ref, not a useEffect dependency: `onClose` changes identity on nearly
  // every caller, because it's typically a function declared fresh in the
  // component body on each render (e.g. a `close()` closure) — including
  // the re-render triggered by typing a single character into a controlled
  // input inside the sheet. If the focus-management effect below depended
  // on `onClose` directly, that keystroke would tear the effect down
  // (restoring focus to whatever was active before the sheet opened, via
  // `prevFocus.current?.focus()` in the cleanup) and set it back up
  // (refocusing the surface), kicking focus out of the field the member was
  // actively typing in. Written in its own effect (never during render —
  // refs are not render-safe writes) so it stays current independent of the
  // focus-management effect, which depends on neither its value nor its
  // identity. Mirrors the identical fix in ui/Dialog/Dialog.tsx
  // (fix/dialog-focus-loss) — same root cause, same shared focus-trap shape.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    prevFocus.current = document.activeElement as HTMLElement;
    const surface = surfaceRef.current;
    surface?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && surface) {
        const focusables = surface.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        const withinList = Array.prototype.includes.call(focusables, active);
        if (!withinList) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      prevFocus.current?.focus();
    };
  }, [open]);

  return surfaceRef;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Sheet({
  side,
  open,
  onClose,
  title,
  children,
  width,
  closeLabel,
  renderHeader,
}: SheetProps) {
  const { prefs } = usePreferences();
  const titleId = useId();
  const surfaceRef = useFocusTrap(open, onClose);

  if (!open) return null;

  const Surface =
    side === 'center' ? CenterSurface : side === 'bottom' ? BottomSurface : RightSurface;
  const labelledBy = title ? titleId : undefined;

  return createPortal(
    <>
      <Scrim
        data-testid="sheet-scrim"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        aria-hidden="true"
      />
      <Surface
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ? undefined : 'Panel'}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        data-testid="sheet-surface"
        data-custom-header={renderHeader ? 'true' : undefined}
        $width={width}
        style={prefs.reduceMotion ? { animation: 'none' } : undefined}
      >
        {renderHeader ? (
          renderHeader(titleId)
        ) : (
          <>
            {side !== 'right' && <Handle aria-hidden="true" $responsive={side === 'center'} />}
            {title && <SheetTitle id={titleId}>{title}</SheetTitle>}
            {closeLabel && (
              <CloseButton type="button" onClick={onClose} aria-label={closeLabel}>
                <Icon name="close" size={20} />
              </CloseButton>
            )}
          </>
        )}
        {renderHeader ? <HeaderBody>{children}</HeaderBody> : children}
      </Surface>
    </>,
    document.body,
  );
}
